import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { ON_SIGNUP_ERROR_MESSAGE, GIFTING_QUERY_PARAM, ALT_CAPTCHA_ERROR_MESSAGE } from 'Utils/Constants';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { hasEntitlementCookie } from 'Utils/Entitlements';
import {
  getExistingUserRedirects,
  getSheerVerificationId,
  incrementExistingUserRedirects,
  isSheerIdPlan,
} from 'Utils/SheerIdHelpers';
import { isDefaultPlanFlow } from './Analytics/Amplitude';
import { getDefaultConfig, getStoreDispatch, getStoreState } from 'Utils/Store';
import { isSheerProductSKU, getSheerProgramFromSKU } from 'Utils/SheerIdHelpers';
import { getTokenExpirations, isNonAnonymousUser } from './UserCookie';
import { createPaymentAccount } from 'Api/ProfileApi';
import { setUser } from 'Utils/UserCookie';
import { heapIdentify } from 'Utils/Analytics/Heap';
import { setBrazeUser } from 'Utils/Braze';
import { signIn, getEntitlements } from 'State/User/UserActions';
import { fetchSubscriptionStatus } from 'State/Subscriptions/SubscriptionsActions';
import { getUserSheerInfo } from 'State/Integrations/IntegrationsActions';

export const createUserAccount = async (values, onSignInSuccess, onSignUpSuccess, analyticTraits) => {
  let recaptchaToken;
  const { signInCaptchaEnabled, signInCaptchaSiteKey } = getDefaultConfig();
  if (signInCaptchaEnabled && window.grecaptcha) {
    recaptchaToken = await window.grecaptcha.execute(signInCaptchaSiteKey, { action: 'submit' });
  }
  const res = await createPaymentAccount(values, recaptchaToken);
  const { accessToken, refreshToken, tokenExpiration, cpCustomerID, userType } = res;
  const expirations = getTokenExpirations(tokenExpiration);

  const { firstName = '', lastName = '', email } = values;
  const userData = {
    ...res,
    firstName,
    lastName,
    email,
    accessToken,
    refreshToken,
    ...expirations,
    profileId: cpCustomerID,
  };

  if (accessToken) {
    await onSignInSuccess(userData, true);
    const utagData = {
      profile_type: userType,
      newsletter_opt_in: 'yes',
      dcg_profile_id: cpCustomerID,
    };

    trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_COMPLETED, { ...analyticTraits }, utagData);

    return onSignUpSuccess();
  }

  const { failureMessage } = res;
  const errorCode = (failureMessage && failureMessage.errorCode) || 'server-side';

  let errorMsg = ON_SIGNUP_ERROR_MESSAGE.onVerificationError;
  if (failureMessage?.errorMessage) {
    errorMsg = failureMessage.errorMessage.includes('reCaptcha')
      ? ALT_CAPTCHA_ERROR_MESSAGE
      : failureMessage.errorMessage;
  }

  trackEvent(
    TRACKING_ENUMS.EVENT_SIGNUP_ERROR,
    {
      page_error_type: errorCode,
      page_error_description: errorMsg,
      ...analyticTraits,
    },
    {
      dcg_profile_id: cpCustomerID,
      error_type: errorCode,
      error_message: errorMsg,
    }
  );
  return onSubmitReactFormError(errorMsg, errorCode);
};

export const signInRedirect = async (entitlementFromSignIn, location) => {
  const {
    appConfig: { defaultConfig, routes },
    purchaseMode: { requirePlanSelection, isGifting = false },
    secondScreen: { isDeviceActivation },
    subscription,
    integrations: { bango },
  } = getStoreState();

  let isExternalRedirect, redirectTo;

  const { rootDomain, hideHasSubScreenOnSignUp, alwaysRefreshOnRootSignin } = defaultConfig;
  const { subscriptionId: isBango, productId: bangoProductId } = bango;

  let search = isGifting ? GIFTING_QUERY_PARAM : '';
  // set default path
  let path = '/';
  let step = '';
  let state = {};
  let redirectUrl = '';

  // if return_to param is passed to the page
  const query = new URLSearchParams(location.search);
  const returnUrl = query.get('return_to');
  const hasEntitlement = hasEntitlementCookie() || entitlementFromSignIn;

  if (isGifting) {
    redirectTo = routes.giftingReviewAndRedeem;
  }

  if (isDeviceActivation) {
    if (!hasEntitlement) {
      redirectTo = routes.signupPlans;
    } else {
      redirectTo = routes.secondScreenSuccess;
      isExternalRedirect = true;
    }
  }

  if (returnUrl) {
    isExternalRedirect = !!returnUrl.match(/http/i);
    redirectTo = returnUrl;
  }

  // if redirect has specific path
  if (redirectTo) {
    path = redirectTo;
    if (hasEntitlement && !isDeviceActivation && !isGifting && !isExternalRedirect) {
      path = rootDomain;
      if (hideHasSubScreenOnSignUp || !!path.match(/http/i)) {
        isExternalRedirect = true;
      }
    }
  } else {
    const from = location?.state?.from || { pathname: '/', search: '' };
    const isRootUrl = from.pathname === '/';
    if (isRootUrl && !hasEntitlement && requirePlanSelection) {
      path = routes.signupPlans;
    } else if (isRootUrl && document.referrer !== '') {
      path = rootDomain;
      isExternalRedirect = true;
    } else {
      path = from.pathname;
      search = from.search;
      state = from.state;
    }
  }

  // check if alwaysRefreshOnRootSignin for d2c products only
  if (path === '/' && alwaysRefreshOnRootSignin) {
    isExternalRedirect = true;
  }
  if (isDeviceActivation && isExternalRedirect) {
    window.location.replace(path);
    return {
      isExternalRedirect,
    };
  }

  if (hasEntitlement && returnUrl && returnUrl.includes(routes.signupCreateAccount)) {
    if (subscription.isIAPUser && subscription.hasActiveSubscription && getSheerVerificationId()) {
      path = routes.signupCurrentSubscriberMilitaryFirst;
    } else {
      path = returnUrl;
    }
    isExternalRedirect = false;
  }

  if (isDefaultPlanFlow() && !subscription.hasActiveSubscription && !returnUrl) {
    path = routes.payment;
  }

  if (isBango) {
    path = routes.bangoFinal;
    if (isSheerProductSKU(bangoProductId)) {
      const sheerProgramType = getSheerProgramFromSKU(bangoProductId);
      if (sheerProgramType) {
        path = routes.sheerIdVerification;
        search = `sheerVerification=${sheerProgramType}`;
      } else {
        path = routes.sheerIdVerification;
      }
    }
  }

  //For when an non-sheerid existing user is in the middle of the sheerid verification process
  const existingUserThreeTimes =
    subscription.hasActiveSubscription &&
    !isSheerIdPlan(subscription.activeSubscriptionId) &&
    !(subscription.purchaseType === 'bango') &&
    getExistingUserRedirects() < 3;
  if (isNonAnonymousUser() && (subscription.isNewUser || subscription.isChurnedUser || existingUserThreeTimes)) {
    try {
      const dispatch = getStoreDispatch();
      const response = await dispatch(getUserSheerInfo());
      if (response?.verificationId) {
        path = routes.sheerIdVerification;
        if (response?.program) {
          path = `${routes.sheerIdVerification}`;
          search = `?sheerVerification=${response.program}`;
        }
        step = response?.currentStep;
        redirectUrl = response?.bangoRedirectUrl ?? '';
        if (existingUserThreeTimes) incrementExistingUserRedirects();
      }
    } catch (error) {
      if (error?.verificationId) {
        path = routes.sheerIdVerification;
        if (error?.program) {
          path = `${routes.sheerIdVerification}`;
          search = `?sheerVerification=${error.program}`;
        }
        step = error?.currentStep;
        redirectUrl = error?.bangoRedirectUrl ?? '';
        if (existingUserThreeTimes) incrementExistingUserRedirects();
      }
    }
  }

  /*
    This happens when create account page has the sheerVerification param in url after a logged out user
    that hasn't started verification is redirected there from sheer verification page. This will redirect
    them back to verification page to start verification.
  */
  if (path.includes('?sheerVerification=') && !path.includes(routes.sheerIdVerification)) {
    search = `?sheerVerification=${path.split('sheerVerification=')[1]}`;
    path = `${routes.sheerIdVerification}`;
  }

  if (isExternalRedirect) {
    window.location.replace(path);
    return {
      isExternalRedirect,
    };
  } else {
    return {
      redirectPath: path,
      redirectSearch: search,
      redirectState: state,
      redirectStep: step,
      redirectUrl,
    };
  }
};

export const handleSignInSuccess = () => {
  return async (userData, isInSignUp) => {
    const dispatch = getStoreDispatch();

    await setUser(userData);
    const { profileId } = userData;
    heapIdentify(profileId);
    setBrazeUser(profileId);

    const { hasEntitlement } = await dispatch(getEntitlements(1));
    await dispatch(fetchSubscriptionStatus());
    /**
     * please make sure we call getEntitlements before signIn since we need to check the entitlement
     * in renderRedirect SignInContainer.js; if we call signIn before getEntitlements,
     * it triggers component re-render and entitlementCookie is not set
     */
    dispatch(signIn(userData));
    if (!isInSignUp) trackEvent(TRACKING_ENUMS.EVENT_LOGIN_COMPLETE);

    return hasEntitlement;
  };
};

export const handleSignUpSuccess = (isSheerIdVerification, urlParams, navigate) => {
  return () => {
    const { appConfig, integrations, plans, purchaseMode } = getStoreState();
    const { isGifting } = purchaseMode;
    const { routes } = appConfig;
    const { subscriptionId: isBango, productId: bangoProductId } = integrations.bango;
    const { isPlanSelected, selectedPlan } = plans;

    let path = routes.payment;
    if (isBango) {
      path = routes.bangoFinal;
      if (isSheerProductSKU(bangoProductId)) {
        const sheerProgramType = getSheerProgramFromSKU(bangoProductId);
        path = sheerProgramType
          ? `${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`
          : routes.sheerIdVerification;
      }
    } else if (isSheerIdVerification) {
      const sheerProgramType = urlParams.get('sheerVerification');
      path = sheerProgramType
        ? `${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`
        : routes.sheerIdVerification;
    } else if (isGifting) {
      path = routes.giftingReviewAndRedeem;
    } else if (isPlanSelected && selectedPlan && selectedPlan.hasMerchandise) {
      path = routes.shipping;
    } else if (!isPlanSelected) {
      path = routes.signupPlans;
    }
    navigate(path);
  };
};
